import dayjs, { Dayjs } from 'dayjs';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPostcodeExample } from '@/modules/common/utils/regex.ts';
import { validationSchemaJointlyInvestorDetails } from '@/modules/entity/components/individual/jointly/JointlyInvestorDetailsPage.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { titleOptions } from '@/modules/entity/static/titles.ts';
import { JointlyInvestorDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { CalendarInput, CalendarInputProps, Select, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { CountryCodeEnum, UpdateEntityRequest } from 'api';

const initialValues: JointlyInvestorDetails = {
  address: '',
  title: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  gender: '',
  date_of_birth: '',
  suburb: '',
  state: '',
  postcode: '',
  country: CountryCodeEnum.AUS,
};

export const JointlyInvestorDetailsPageHolder = ({
  moveForward,
  allowSkip,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const generateInitialValues = (): JointlyInvestorDetails => {
    return {
      ...initialValues,
      ...(dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly || {}),
      country:
        dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly?.country ||
        dataHolder?.data?.new_data?.entity_data?.country,
    };
  };

  const formik = useFormik<JointlyInvestorDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaJointlyInvestorDetails,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            individual_entity_details: {
              ...dataHolder?.data?.new_data?.entity_data?.individual_entity_details,
              jointly: {
                ...dataHolder?.data?.new_data?.entity_data?.individual_entity_details?.jointly,
                ...values,
              },
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    formik.setFieldValue('date_of_birth', date ? date.format('YYYY-MM-DD').toString() : '');
    formik.setFieldError('date_of_birth', '');
  };

  const handleChangeSelect = (field: string, value: any) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  return (
    <OnboardingHolderWrapper
      title="Jointly investor details"
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      formik={formik}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-10 justify-start">
        <div className="w-full flex items-start space-x-2">
          <Select
            isClearable
            label="gender"
            options={genderOptions}
            value={formik.values?.gender}
            onChange={value => handleChangeSelect('gender', value || '')}
            error={formik.errors?.gender}
          />
          <Select
            label="title"
            options={titleOptions}
            isClearable
            value={formik.values.title}
            defaultValue={formik.values.title}
            onChange={value => handleChangeSelect('title', value || '')}
            error={formik.errors?.title}
          />
          <TextInputField label="first name" name={`first_name`} />
        </div>
        <div className="w-full flex items-start space-x-2">
          <TextInputField label="Middle name (optional)" name={`middle_name`} />
          <TextInputField label="Last name" name={`last_name`} />
        </div>
        <CalendarInput
          name="date_of_birth"
          onChange={handleChangeDate}
          label="date of birth"
          defaultValue={
            formik.values.date_of_birth ? dayjs(formik.values.date_of_birth) : undefined
          }
          error={formik.errors?.date_of_birth}
          disabledDate={(date: Dayjs) => date.isAfter() && !date.isSame(dayjs(), 'day')}
        />
        <Select
          name="country"
          label="country"
          options={countryOptions}
          value={formik.values.country}
          onChange={value => handleChangeSelect('country', value)}
          error={formik.errors.country}
          isClearable={true}
        />
        <TextInputField name="address" label="Address: Unit / Street Number / Street Name" />
        <div className="w-full flex items-start justify-between space-x-6">
          <TextInputField name="suburb" label="suburb" />

          {formik.values.country === 'AUS' && (
            <Select
              name="state"
              label="state"
              options={auStateOptions}
              value={formik.values.state}
              onChange={value => handleChangeSelect('state', value)}
              error={formik.errors.state}
              isClearable={true}
            />
          )}
          <TextInputField
            name="postcode"
            label="postcode"
            placeholder={getPostcodeExample(formik.values.country)}
          />
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
